import { Fragment, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import type { SpecSection } from "./specSections";

export interface SpecFallbackRow {
  label: string;
  value: string;
}

interface ProductSpecsProps {
  sections: SpecSection[];
  /** Rendered only when there are no grouped sections (e.g. enrichment unavailable). */
  fallbackRows: SpecFallbackRow[];
}

function highlightSearchMatch(text: string, filter: string) {
  const query = filter.trim();
  if (!query) {
    return text;
  }

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const matcher = new RegExp(`(${escapedQuery})`, "gi");

  return text.split(matcher).map((part, index) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <Box
        key={index}
        component="mark"
        sx={{
          bgcolor: "primary.12p",
          color: "inherit",
          px: 0.125,
        }}
      >
        {part}
      </Box>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    ),
  );
}

/** Renders grouped product specs with client-side search, or a simple fallback list. */
export function ProductSpecs({ sections, fallbackRows }: ProductSpecsProps) {
  const [filter, setFilter] = useState("");

  const filteredSections = useMemo(() => {
    const query = filter.toLowerCase().trim();
    if (!query) return sections;
    return sections
      .map((section) => ({
        ...section,
        groups: section.groups
          .map((group) => ({
            ...group,
            attributes: group.attributes.filter((attr) => {
              const displayName = group.subHeading
                ? `${group.subHeading} / ${attr.label}`
                : attr.label;

              return (
                displayName.toLowerCase().includes(query) ||
                attr.values.some((value) => value.toLowerCase().includes(query)) ||
                section.heading.toLowerCase().includes(query)
              );
            }),
          }))
          .filter((group) => group.attributes.length > 0),
      }))
      .filter((section) => section.groups.length > 0);
  }, [sections, filter]);

  if (sections.length === 0) {
    if (fallbackRows.length === 0) return null;
    return (
      <Box sx={{ width: "100%", minWidth: 0, px: 2.5, pb: 2.5, pt: 1.5 }}>
        <Typography
          variant="subtitle2"
          fontWeight={400}
          sx={{ mb: 1.25, fontSize: 16 }}
        >
          Product Specs
        </Typography>
        {fallbackRows.map((row) => (
          <Box key={row.label} sx={{ display: "flex", minWidth: 0, py: 0.5 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ width: "45%", flexShrink: 0, overflowWrap: "anywhere" }}
            >
              {row.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{ minWidth: 0, overflowWrap: "anywhere" }}
            >
              {row.value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        overflowX: "hidden",
        wordBreak: "break-word",
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "background.paper",
          px: 2.5,
          pt: 1.5,
          pb: 1.25,
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={400}
          sx={{ mb: 1, fontSize: 16 }}
        >
          Product Specs
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Search specs..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 15, color: "text.disabled" }} />
                </InputAdornment>
              ),
              endAdornment: filter ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setFilter("")}
                    aria-label="Clear spec search"
                    sx={{ p: 0.25 }}
                  >
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: { fontSize: "0.78rem", height: 30 },
            },
          }}
          sx={{ "& .MuiOutlinedInput-root": { bgcolor: "background.default" } }}
        />
      </Box>

      <Box sx={{ width: "100%", minWidth: 0, px: 2.5, pt: 1.5, pb: 2.5 }}>
        {filteredSections.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 2, textAlign: "center" }}
          >
            No specs match &ldquo;{filter}&rdquo;
          </Typography>
        ) : (
          filteredSections.map((section) => (
            <Box key={section.heading} sx={{ mb: 3 }}>
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{
                  display: "block",
                  fontSize: 11,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: "text.primary",
                  mb: 1,
                  pb: 0.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                {highlightSearchMatch(section.heading, filter)}
              </Typography>
              {section.groups.map((group, groupIndex) => (
                <Box
                  key={group.subHeading ?? "__ungrouped__"}
                  sx={{ mt: group.subHeading && groupIndex > 0 ? 1.5 : 0 }}
                >
                  {group.subHeading && (
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        fontSize: 11,
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                        color: "text.secondary",
                        mb: 0.5,
                      }}
                    >
                      {highlightSearchMatch(group.subHeading, filter)}
                    </Typography>
                  )}
                  {group.attributes.map((attr) => (
                    <Box
                      key={attr.label}
                      sx={{
                        display: "flex",
                        minWidth: 0,
                        py: 0.375,
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          width: "44%",
                          flexShrink: 0,
                          pr: 1,
                          lineHeight: 1.5,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {highlightSearchMatch(attr.label, filter)}
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {attr.values.length === 1 ? (
                          <Typography
                            variant="body2"
                            sx={{ lineHeight: 1.5, minWidth: 0, overflowWrap: "anywhere" }}
                          >
                            {highlightSearchMatch(attr.values[0], filter)}
                          </Typography>
                        ) : (
                          <Box
                            component="ul"
                            sx={{
                              m: 0,
                              pl: 1.75,
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.125,
                            }}
                          >
                            {attr.values.map((value) => (
                              <Typography
                                key={value}
                                component="li"
                                variant="body2"
                                sx={{ lineHeight: 1.5, overflowWrap: "anywhere" }}
                              >
                                {highlightSearchMatch(value, filter)}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}
